/**
 * The picture library.
 *
 * Search, tags and paging, because a library of two hundred pictures called
 * IMG_4821.jpg is not a library. The size shown is the size it will *print* at,
 * in millimetres - the number that decides whether a picture works on paper.
 *
 * Deleting says how many questions use the picture first. "Remove" on something
 * eleven questions depend on deserves to say so.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Image } from '../../lib/api/client'
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Toolbar,
  Select,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { Picture } from '../../components/Picture'
import { toast } from '../../components/toast'
import { PictureGenerator } from '../builder/PictureGenerator'
import './pictures.css'

const PER_PAGE = 24

export function Pictures() {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [tag, setTag] = useState('')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<Image | null>(null)
  const [importUrl, setImportUrl] = useState('')

  const library = useQuery({
    queryKey: ['pictures', query, tag, page],
    queryFn: () => api.pictures({ q: query, tags: tag, page, per_page: PER_PAGE }),
  })
  const tags = useQuery({ queryKey: ['picture-tags'], queryFn: api.pictureTags })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['pictures'] })
    queryClient.invalidateQueries({ queryKey: ['picture-tags'] })
  }
  const failed = (error: Error) => toast(error.message, 'error')

  const upload = useMutation({ mutationFn: api.uploadPictures, onSuccess: refresh, onError: failed })
  const importUrlMutation = useMutation({
    mutationFn: () => api.importPicture(importUrl),
    onSuccess: () => {
      setImportUrl('')
      refresh()
    },
    onError: failed,
  })

  if (library.isLoading) return <Spinner label="Loading the picture library" />

  const result = library.data
  const images = result?.images ?? []

  return (
    <div className="tc-page tc-pictures-page">
      <PageHeader
        eyebrow="Shared school library"
        title="Pictures"
        icon={<Icon.pictures size={20} />}
        description="Find, upload, label, and generate pictures shared by every teacher at your school."
      />

      <Toolbar>
        <div className="tc-picture-tool-group" role="search" aria-label="Search pictures">
        <Input
          aria-label="Search pictures"
          placeholder="Search by name or label"
          value={query}
          style={{ width: 220 }}
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(1)
          }}
        />
        <Select
          aria-label="Filter by label"
          className="tc-picture-filter"
          value={tag}
          onChange={(event) => {
            setTag(event.target.value)
            setPage(1)
          }}
        >
          <option value="">All labels</option>
          {(tags.data ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        </div>
        <div className="tc-spacer" />
        <div className="tc-picture-tool-group" aria-label="Add pictures">
        <Input
          aria-label="Picture address to import"
          placeholder="Paste a picture address"
          value={importUrl}
          style={{ width: 220 }}
          onChange={(event) => setImportUrl(event.target.value)}
        />
        <Button
          size="sm"
          disabled={!importUrl.trim() || importUrlMutation.isPending}
          onClick={() => importUrlMutation.mutate()}
        >
          Import
        </Button>

        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*"
          hidden
          onChange={(event) => {
            // Array.from rather than spreading: a FileList is array-like but not
            // iterable under this TypeScript target.
            const files = event.target.files ? Array.from(event.target.files) : []
            if (files.length) upload.mutate(files)
            event.target.value = ''
          }}
        />
        <Button
          variant="primary"
          size="sm"
          icon={<Icon.plus size={15} />}
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          Upload
        </Button>
        </div>
      </Toolbar>

      {/* The same generator the question editor uses, so a teacher who thinks
          "I need a picture" while looking at the library does not have to open a
          question to make one. */}
      <PictureGenerator library={images} onSaved={refresh} />

      <p className="tc-field-hint" style={{ margin: 0 }}>
        Every picture here belongs to your school and is visible to all its teachers, including
        any made with AI. Only use pictures you are allowed to use — many images on the web are
        copyrighted, even when they are easy to download.
      </p>

      {images.length === 0 ? (
        <EmptyState
          icon={<Icon.pictures size={20} />}
          title={query || tag ? 'Nothing matches' : 'No pictures yet'}
          message={
            query || tag
              ? 'Try a different word, or clear the label filter.'
              : 'Pictures are shared with everyone at your school, so nobody has to find the same one twice.'
          }
        />
      ) : (
        <>
          <div className="tc-picture-grid">
            {images.map((image) => (
              <figure key={image.id} className="tc-picture">
                {/* No `loading="lazy"`: Picture has already fetched the bytes to make the
                    object URL, so lazy loading saves nothing and only delays paint. */}
                <Picture id={image.id} alt={image.original_filename} />
                <figcaption>
                  <span className="tc-picture-name" title={image.original_filename}>
                    {image.original_filename}
                  </span>
                  {/* The size it will actually print at - a proper multiplication
                      sign, because this is a dimension a teacher reads. */}
                  <span className="tc-field-hint">
                    {Math.round(image.width_mm)} × {Math.round(image.height_mm)} mm
                  </span>
                  {image.tags.length > 0 && (
                    <span className="tc-picture-tags">{image.tags.join(', ')}</span>
                  )}
                </figcaption>
                <Button size="sm" onClick={() => setEditing(image)}>
                  Labels
                </Button>
              </figure>
            ))}
          </div>

          {result && result.page_count > 1 && (
            <div className="tc-pager">
              <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="tc-field-hint">
                Page {result.page} of {result.page_count} · {result.total} pictures
              </span>
              <Button
                size="sm"
                disabled={page >= result.page_count}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {editing && <PictureDialog image={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  )
}

function PictureDialog({
  image,
  onClose,
  onSaved,
}: {
  image: Image
  onClose: () => void
  onSaved: () => void
}) {
  const [tags, setTags] = useState(image.tags.join(', '))
  const usage = useQuery({ queryKey: ['usage', image.id], queryFn: () => api.pictureUsage(image.id) })

  const save = useMutation({
    mutationFn: () =>
      api.setPictureTags(
        image.id,
        tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      ),
    onSuccess: () => {
      onSaved()
      onClose()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const remove = useMutation({
    mutationFn: () => api.deletePicture(image.id),
    onSuccess: () => {
      onSaved()
      onClose()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const used = usage.data ?? 0

  return (
    <Dialog
      open
      title={image.original_filename}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {used > 0 ? `Remove (used ${used}×)` : 'Remove'}
          </Button>
          <div className="tc-spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Save labels
          </Button>
        </>
      }
    >
      <Picture className="tc-picture-large" id={image.id} />
      <Field
        label="Labels"
        hint="Separated by commas, for example: shapes, year 2. Labels are how it gets found again."
      >
        <Input value={tags} onChange={(event) => setTags(event.target.value)} />
      </Field>
      <p className="tc-field-hint" style={{ margin: 0 }}>
        Prints at {Math.round(image.width_mm)} × {Math.round(image.height_mm)} mm.{' '}
        {used > 0
          ? `Used by ${used} question${used === 1 ? '' : 's'} — those papers keep it if you remove it here.`
          : 'Not used by any question yet.'}
      </p>
    </Dialog>
  )
}
